import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DsBannerState, DsButtonVariants, DsModule } from '@levelaccess/design-system';
import { StatusCodes } from 'http-status-codes';
import { Subscription } from 'rxjs';

import { IOAuthInteractionDetails, IOAuthOrganizationResponse } from '@shared/interfaces/oauth.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';

import { SharedModule } from '../../../../shared/shared.module';
import { OAuthService } from '../../../../services/oauth.service';
import { LoginCardComponent } from '../../login/login-card/login-card.component';
import { FormService } from '../../../../services/form.service';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { TranslateService } from '../../../../translate/translate.service';
import { ErrorMessageService } from '../../../../services/error-message.service';
import { isSafeNavigationUrl } from '../oauth-navigation.utility';

enum FormField {
  organizationUrl = 'organizationUrl',
}

/** The first part of the organization's address is the organization: what the authorization server looks up. */
const getOrganization = (organizationUrl: string): string | null => {
  try {
    const [, host] = SharedCommonUtility.getProtocolHostPortAndPath(organizationUrl);
    return String(host).split('.')[0].toLowerCase();
  } catch {
    return null;
  }
};

/** The domain validator accepts some values that are no address, such as a port that is not a number: the same message applies. */
const organizationValidator = (control: AbstractControl): ValidationErrors | null =>
  SharedCommonUtility.notNullishOrEmpty(control.value) && getOrganization(control.value) === null
    ? { isDomainValid: `"${control.value}"` }
    : null;

/**
 * First step of connecting an application (for example an AI assistant) to Level Access: the user says which organization
 * they belong to, by its web address, and continues to that organization's own sign-in page.
 */
@Component({
  selector: 'app-oauth-organization',
  templateUrl: './oauth-organization.component.html',
  standalone: true,
  imports: [SharedModule, DsModule, ReactiveFormsModule, LoginCardComponent],
})
export class OAuthOrganizationComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly FormField: typeof FormField = FormField;
  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;

  @ViewChild('organizationInput') public organizationInput: ElementRef<HTMLInputElement>;

  public form: UntypedFormGroup;
  public formFieldErrors: Partial<Record<FormField, string>> = {};
  public details: IOAuthInteractionDetails | null = null;
  /** The link is old, was already used, or was opened in another browser than the one that started the connection. */
  public isLinkNotValid: boolean = false;
  public submitError: string | null = null;
  public isSubmitting: boolean = false;

  private readonly uid: string | undefined;
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private formService: FormService,
    private oauthService: OAuthService,
    private translateService: TranslateService,
    private errorMessageService: ErrorMessageService,
    private element: ElementRef<Element>,
    activatedRoute: ActivatedRoute,
  ) {
    this.uid = activatedRoute.snapshot.queryParams['uid'];
    this.form = this.formBuilder.group({
      [FormField.organizationUrl]: this.formBuilder.control('', {
        validators: [CustomValidators.required, CustomValidators.domainValidator, organizationValidator],
      }),
    });
  }

  public ngOnInit(): void {
    if (SharedCommonUtility.isNullishOrEmpty(this.uid)) {
      this.isLinkNotValid = true;
      return;
    }

    this.subscriptions.add(
      this.oauthService.getInteractionDetails(this.uid).subscribe({
        next: (details: IOAuthInteractionDetails): void => {
          this.details = details;
        },
        error: (error: HttpErrorResponse): void => {
          // Only a refusal means the link is bad. The details just name the application, so the page still works without them
          this.isLinkNotValid = error.status === StatusCodes.BAD_REQUEST;
        },
      }),
    );
  }

  /** The browser may restore this page from its back/forward cache after the user came back from the sign-in page. */
  @HostListener('window:pageshow', ['$event'])
  public onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.isSubmitting = false;
    }
  }

  public ngAfterViewInit(): void {
    this.organizationInput?.nativeElement.focus();
  }

  private checkValidity(): boolean {
    this.formFieldErrors = {};
    this.submitError = null;

    // People paste addresses with spaces around them, and spaces alone are no address
    const control: AbstractControl = this.form.get(FormField.organizationUrl);
    control.setValue(String(control.value ?? '').trim());

    if (this.form.valid && SharedCommonUtility.notNullishOrEmpty(control.value)) {
      return true;
    }

    this.formFieldErrors[FormField.organizationUrl] = this.formService.getErrorMessageForField(
      control,
      FormField.organizationUrl,
      '',
      '',
      { required: this.translateService.instant('enter_a_url') },
    );
    this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);

    return false;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onSubmit(): void {
    if (this.isSubmitting || !this.checkValidity()) {
      return;
    }

    this.isSubmitting = true;
    // The validators ruled out a value the organization cannot be read from
    const organization: string = getOrganization(this.form.get(FormField.organizationUrl).value) ?? '';

    this.subscriptions.add(
      this.oauthService.submitOrganization(this.uid, organization).subscribe({
        next: (response: IOAuthOrganizationResponse): void => {
          if (!isSafeNavigationUrl(response.consentUrl)) {
            this.isSubmitting = false;
            this.submitError = this.translateService.instant('oauth_something_went_wrong');
            return;
          }
          // A plain navigation: the organization's own sign-in page takes over (password and MFA, or its single sign-on)
          window.open(response.consentUrl, '_self');
        },
        error: (error: HttpErrorResponse): void => {
          this.isSubmitting = false;

          if (error.status === StatusCodes.NOT_FOUND) {
            this.formFieldErrors[FormField.organizationUrl] = this.translateService.instant('oauth_organization_not_available');
            this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
          } else if (error.status === StatusCodes.BAD_REQUEST) {
            this.isLinkNotValid = true;
          } else if (error.status === StatusCodes.TOO_MANY_REQUESTS) {
            this.submitError = this.translateService.instant('oauth_too_many_attempts');
          } else {
            this.submitError = this.translateService.instant('oauth_something_went_wrong');
          }
        },
      }),
    );
  }
}
